import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'

/**
 * СПИСКИ ПОКУПОК
 *
 * Свои и те, что открыли другие, — вперемешку, но с пометкой чьи.
 * Разделять их на две группы незачем: человек ищет «список для Пятёрочки»,
 * а не «список Анны».
 */

type ShoppingList = {
  id: string
  name: string
  icon: string | null
  store: { id: string; name: string; color: string | null } | null
  owner: { id: string; displayName: string; avatarUrl: string | null }
  mine: boolean
  total: number
  done: number
}

type Store = { id: string; name: string; color: string | null }

export function ShoppingPage() {
  const [lists, setLists] = useState<ShoppingList[] | null>(null)
  const [stores, setStores] = useState<Store[]>([])
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [store, setStore] = useState('')

  const load = () => {
    api<{ lists: ShoppingList[] }>('/api/shopping')
      .then((data) => setLists(data.lists))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }

  useEffect(load, [])

  // Магазины заводятся один раз на семью и потом подсказываются:
  // «Пятёрочка» и «пятерочка» не должны стать двумя разными
  useEffect(() => {
    api<{ stores: Store[] }>('/api/shopping/stores/all')
      .then((data) => setStores(data.stores))
      .catch(() => setStores([]))
  }, [])

  const create = async () => {
    const title = name.trim()
    if (!title) return

    try {
      // Магазин указали словом — заводим его, если такого ещё нет
      let storeId: string | null = null
      const picked = store.trim()
      if (picked) {
        const known = stores.find((s) => s.name.toLowerCase() === picked.toLowerCase())
        if (known) {
          storeId = known.id
        } else {
          const made = await api<{ store: Store }>('/api/shopping/stores/all', {
            method: 'POST',
            body: { name: picked },
          })
          storeId = made.store.id
          setStores((current) => [...current, made.store])
        }
      }

      await api('/api/shopping', { method: 'POST', body: { name: title, storeId } })
      setName('')
      setStore('')
      setCreating(false)
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось создать')
    }
  }

  return (
    <div className="section">
      <header className="section-head">
        <h1>Покупки</h1>
        <button className="icon-btn" onClick={() => setCreating(true)} aria-label="Новый список">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
            <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
          </svg>
        </button>
      </header>

      <div className="section-body scroll">
        {lists === null && !error && (
          <div className="center-screen">
            <div className="spinner" />
          </div>
        )}

        {lists?.length === 0 && (
          <p className="empty-hint">
            Списков пока нет. Заведи первый — например, «Пятёрочка» или «Хозтовары».
          </p>
        )}

        <div className="card-grid">
          {lists?.map((list) => (
            <NavLink key={list.id} to={`/shopping/${list.id}`} className="list-card">
              <div className="list-card-top">
                <span className="list-card-icon" aria-hidden="true">
                  {list.icon ?? '🛒'}
                </span>
                <span className="list-card-name">{list.name}</span>
              </div>

              {list.store && <div className="list-card-store">{list.store.name}</div>}

              <div className="list-card-progress">
                <div
                  className="list-card-fill"
                  style={{ width: list.total ? `${(list.done / list.total) * 100}%` : '0%' }}
                />
                <span className="list-card-count">
                  {list.total === 0 ? 'пусто' : `${list.done} из ${list.total}`}
                </span>
              </div>

              {!list.mine && (
                <div className="list-card-owner">
                  <Avatar
                    id={list.owner.id}
                    name={list.owner.displayName}
                    url={list.owner.avatarUrl}
                    size={20}
                  />
                  <span>{list.owner.displayName}</span>
                </div>
              )}
            </NavLink>
          ))}
        </div>

        {error && <div className="sheet-error">{error}</div>}
      </div>

      {creating && (
        <div className="sheet" onClick={() => setCreating(false)}>
          <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head">
              <h2>Новый список</h2>
              <button className="icon-btn" onClick={() => setCreating(false)} aria-label="Закрыть">
                ✕
              </button>
            </div>

            <label className="field">
              <span className="field-label">Название</span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void create()}
                placeholder="Пятёрочка, Хозтовары, Дача…"
                maxLength={64}
                autoFocus
              />
            </label>

            <label className="field">
              <span className="field-label">Магазин — если список для конкретного</span>
              <input
                className="input"
                value={store}
                onChange={(e) => setStore(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void create()}
                placeholder="Не обязательно"
                list="stores"
                maxLength={64}
              />
              <datalist id="stores">
                {stores.map((s) => (
                  <option key={s.id} value={s.name} />
                ))}
              </datalist>
            </label>

            <button className="btn-primary" onClick={() => void create()} disabled={!name.trim()}>
              Создать
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
